import { Box, Checkbox, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { updateTenderQtipsCompleted } from '../../services/tenderCoreService';
import type { Tender } from '../../types/tender';

const EMPTY_ARRAY: never[] = [];

// Fast djb2 hash for generating unique keys
const hashKey = (str: string, index: number): string => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return `${(hash >>> 0).toString(36)}-${index}`;
};

interface BidStrategyCardProps {
  tender: Tender;
}

const BidStrategyCard: React.FC<BidStrategyCardProps> = ({ tender }) => {
  const qtipsCompleted = tender.qtipsCompleted || EMPTY_ARRAY;
  const [completedTips, setCompletedTips] = useState<Set<number>>(new Set(qtipsCompleted));

  useEffect(() => {
    setCompletedTips(new Set(qtipsCompleted));
  }, [qtipsCompleted]);

  const renderTipWithMarkdown = (tip: string): React.ReactNode[] => {
    const parts = tip.split(/(\*\*.*\*\*)/g);
    return parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={hashKey(part, partIndex)}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderQtips = () => {
    if (!tender.qtips) {
      return (
        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
          This section will be filled with tips to improve the draft after it is generated.
        </Box>
      );
    }

    const tipsArray = Array.isArray(tender.qtips)
      ? tender.qtips
      : typeof tender.qtips === 'string'
        ? tender.qtips.split('\n')
        : [];

    const validTips = tipsArray.filter((tip: string) => typeof tip === 'string' && tip.trim());

    if (validTips.length === 0) {
      return (
        <Typography variant="body1" sx={{ p: 1 }}>
          Processing tender...
        </Typography>
      );
    }

    return (
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 1,
          pt: 1,
          pb: 1,
          listStyleType: 'none',
          '& li': { display: 'flex', alignItems: 'flex-start' },
        }}
      >
        {validTips.map((tip: string, index: number) => {
          const isCompleted = completedTips.has(index);
          return (
            <li key={hashKey(tip, index)} style={{ paddingBottom: '8px' }}>
              <Checkbox
                size="small"
                checked={isCompleted}
                onChange={() => {
                  const next = new Set(completedTips);
                  if (isCompleted) {
                    next.delete(index);
                  } else {
                    next.add(index);
                  }
                  setCompletedTips(next);
                  if (tender.id || tender.tenderId) {
                    updateTenderQtipsCompleted(
                      tender.id || tender.tenderId || '',
                      Array.from(next)
                    );
                  }
                }}
                sx={{ p: 0, mr: 1, mt: 0.5 }}
              />
              <Typography
                variant="body2"
                sx={{
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  color: isCompleted ? 'text.disabled' : 'text.primary',
                }}
              >
                {renderTipWithMarkdown(tip.trim())}
              </Typography>
            </li>
          );
        })}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: 0, // Removed padding as AccordionDetails handles it
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, minHeight: 0 }}>{renderQtips()}</Box>
    </Box>
  );
};

export default BidStrategyCard;
